import React, { useMemo, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import api from '../Axios/axios';
import Navbar from '../Navbar/Navbar';
import { getToken, getUserRoleFromToken } from '../../utils/auth';
import './ManualInvoiceCreatePage.scss';

const createLine = (index = 0) => ({
  id: `${Date.now()}-${index}`,
  description: '',
  quantity: 1,
  unitPrice: '',
  vatRate: 20
});

const formatCurrency = (value) =>
  new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(Number(value || 0));

const VAT_OPTIONS = [20, 10, 1, 0];
const INVOICE_SENDING_DISABLED = true;

const ManualInvoiceCreatePage = () => {
  const userRole = getUserRoleFromToken(getToken());
  const [taxNo, setTaxNo] = useState('');
  const [invoiceProfile, setInvoiceProfile] = useState('');
  const [draftError, setDraftError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [lookupMessage, setLookupMessage] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [sendError, setSendError] = useState('');
  const [isLookupLoading, setIsLookupLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [customerForm, setCustomerForm] = useState({
    companyName: '',
    taxOffice: '',
    address: '',
    phone: '',
    email: '',
    note: ''
  });
  const [lines, setLines] = useState([createLine(0)]);

  const handleLookup = async () => {
    setDraftError('');
    setLookupMessage('');
    setSendMessage('');
    setSendError('');

    if (!taxNo.trim()) {
      setLookupMessage('Önce vergi numarası girmemiz gerekiyor.');
      return;
    }

    try {
      setIsLookupLoading(true);

      const { data } = await api.post('/NetteFatura/customer-lookup', {
        taxNumber: taxNo.trim()
      });

      setLookupResult(data || null);
      setInvoiceProfile(data?.invoiceProfile || '');
      setCustomerForm((current) => ({
        ...current,
        companyName: data?.companyName || current.companyName,
        taxOffice: data?.taxOffice || current.taxOffice,
        address: data?.address || current.address,
        phone: data?.phone || current.phone,
        email: data?.email || current.email
      }));

      setLookupMessage(
        data?.message ||
          (data?.isSuccess
            ? 'Firma bilgileri başarıyla getirildi.'
            : 'Sorgu tamamlandı, gelen alanları kontrol edebilirsin.')
      );
    } catch (error) {
      setLookupMessage('Vergi numarası sorgusu sırasında bir hata oluştu.');
    } finally {
      setIsLookupLoading(false);
    }
  };

  const updateCustomerField = (field, value) => {
    setDraftError('');
    setSendMessage('');
    setSendError('');
    setCustomerForm((current) => ({
      ...current,
      [field]: value
    }));
  };

  const updateLine = (id, field, value) => {
    setDraftError('');
    setSendMessage('');
    setSendError('');
    setLines((current) =>
      current.map((line) => (line.id === id ? { ...line, [field]: value } : line))
    );
  };

  const addLine = () => {
    setDraftError('');
    setSendMessage('');
    setSendError('');
    setLines((current) => [...current, createLine(current.length)]);
  };

  const removeLine = (id) => {
    setDraftError('');
    setSendMessage('');
    setSendError('');
    setLines((current) => (current.length === 1 ? current : current.filter((line) => line.id !== id)));
  };

  const totals = useMemo(() => {
    const subTotal = lines.reduce((sum, line) => {
      const quantity = Number(line.quantity || 0);
      const unitPrice = Number(line.unitPrice || 0);
      return sum + quantity * unitPrice;
    }, 0);

    const totalVat = lines.reduce((sum, line) => {
      const quantity = Number(line.quantity || 0);
      const unitPrice = Number(line.unitPrice || 0);
      const vatRate = Number(line.vatRate || 0);
      return sum + quantity * unitPrice * (vatRate / 100);
    }, 0);

    return {
      subTotal,
      totalVat,
      total: subTotal + totalVat
    };
  }, [lines]);

  const handlePreview = () => {
    setDraftError('');
    setSendMessage('');
    setSendError('');

    const missingFields = [];

    if (!taxNo.trim()) missingFields.push('Vergi Numarası');
    if (!customerForm.companyName.trim()) missingFields.push('Firma Ünvanı');
    if (!customerForm.address.trim()) missingFields.push('Adres');

    const hasValidLine = lines.some(
      (line) => line.description.trim() && Number(line.quantity || 0) > 0 && Number(line.unitPrice || 0) > 0
    );

    if (!hasValidLine) {
      missingFields.push('en az bir geçerli fatura kalemi');
    }

    if (missingFields.length) {
      setPreviewOpen(false);
      setDraftError(`Önizleme için şu alanları tamamlamamız gerekiyor: ${missingFields.join(', ')}.`);
      return;
    }

    setPreviewOpen(true);
  };

  const handleSendInvoice = async () => {
    setDraftError('');
    setSendMessage('');
    setSendError('');

    const missingFields = [];

    if (!taxNo.trim()) missingFields.push('Vergi Numarası');
    if (!customerForm.companyName.trim()) missingFields.push('Firma Ünvanı');
    if (!customerForm.address.trim()) missingFields.push('Adres');

    const validLines = lines.filter(
      (line) => line.description.trim() && Number(line.quantity || 0) > 0 && Number(line.unitPrice || 0) > 0
    );

    if (!validLines.length) {
      missingFields.push('en az bir geçerli fatura kalemi');
    }

    if (missingFields.length) {
      setSendError(`Fatura kesmeden önce şu alanları tamamlamamız gerekiyor: ${missingFields.join(', ')}.`);
      return;
    }

    try {
      setIsSending(true);

      const payload = {
        taxNumber: taxNo.trim(),
        companyName: customerForm.companyName.trim(),
        taxOffice: customerForm.taxOffice.trim(),
        address: customerForm.address.trim(),
        phone: customerForm.phone.trim(),
        email: customerForm.email.trim(),
        note: customerForm.note.trim(),
        invoiceProfile,
        receiverInboxTag: lookupResult?.inboxTags?.[0] || '',
        receiverInboxTags: lookupResult?.inboxTags || [],
        sourceType:
          userRole === 'Owner' || userRole === 'RentalAccountant'
            ? 'rental'
            : userRole === 'TechnicalServiceAccountant'
              ? 'technical-service'
              : '',
        lines: validLines.map((line) => ({
          description: line.description.trim(),
          quantity: Number(line.quantity || 0),
          unitPrice: Number(line.unitPrice || 0),
          vatRate: Number(line.vatRate || 0)
        }))
      };

      const { data } = await api.post('/NetteFatura/send-manual-invoice', payload);

      if (data?.isSuccess) {
        const details = [data?.message];

        if (data?.invoiceNumber) {
          details.push(`Belge No: ${data.invoiceNumber}`);
        }

        if (data?.ettn) {
          details.push(`ETTN: ${data.ettn}`);
        }

        setSendMessage(details.filter(Boolean).join(' | '));
        setPreviewOpen(false);
      } else {
        setSendError(data?.message || 'Fatura gönderimi başarısız oldu.');
      }
    } catch (error) {
      setSendError(error?.response?.data?.message || 'Fatura gönderimi sırasında bir hata oluştu.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="manual-invoice-page">
      <Navbar />

      <div className="manual-invoice-container">
        <header className="manual-invoice-header">
          <div>
            <h1>Manuel Fatura Oluştur</h1>
          </div>

          <Link to="/accounting/invoice-drafts" className="manual-invoice-back-link">
            Fatura İşlemlerine Dön
          </Link>
        </header>

        <section className="manual-invoice-grid">
          <article className="manual-invoice-card">
            <div className="manual-invoice-card__header">
              <h2>Müşteri Bilgileri</h2>
            </div>

            <div className="manual-invoice-tax-lookup">
              <label>
                <span>Vergi Numarası</span>
                <input
                  type="text"
                  value={taxNo}
                  onChange={(event) => setTaxNo(event.target.value)}
                  placeholder="Vergi numarasını gir"
                />
              </label>

              <button type="button" onClick={handleLookup} disabled={isLookupLoading}>
                {isLookupLoading ? 'Sorgulanıyor...' : 'Bilgileri Getir'}
              </button>
            </div>

            {lookupMessage && <div className="manual-invoice-info">{lookupMessage}</div>}
            {draftError && <div className="manual-invoice-feedback error">{draftError}</div>}
            {sendError && <div className="manual-invoice-feedback error">{sendError}</div>}
            {sendMessage && <div className="manual-invoice-feedback success">{sendMessage}</div>}

            <div className="manual-invoice-profile">
              <span>Fatura Tipi</span>
              <strong>{invoiceProfile || 'Henüz sorgulanmadı'}</strong>
            </div>

            <div className="manual-invoice-form">
              <label>
                <span>Firma Ünvanı</span>
                <input
                  type="text"
                  value={customerForm.companyName}
                  onChange={(event) => updateCustomerField('companyName', event.target.value)}
                />
              </label>

              <label>
                <span>Vergi Dairesi</span>
                <input
                  type="text"
                  value={customerForm.taxOffice}
                  onChange={(event) => updateCustomerField('taxOffice', event.target.value)}
                />
              </label>

              <label>
                <span>Telefon</span>
                <input
                  type="text"
                  value={customerForm.phone}
                  onChange={(event) => updateCustomerField('phone', event.target.value)}
                />
              </label>

              <label>
                <span>E-Posta</span>
                <input
                  type="text"
                  value={customerForm.email}
                  onChange={(event) => updateCustomerField('email', event.target.value)}
                />
              </label>

              <label className="full">
                <span>Adres</span>
                <textarea
                  rows="3"
                  value={customerForm.address}
                  onChange={(event) => updateCustomerField('address', event.target.value)}
                />
              </label>

              <label className="full">
                <span>Not</span>
                <textarea
                  rows="3"
                  value={customerForm.note}
                  onChange={(event) => updateCustomerField('note', event.target.value)}
                  placeholder="İstersen faturaya düşecek notu buraya ekleyebilirsin."
                />
              </label>
            </div>
          </article>

          <article className="manual-invoice-card">
            <div className="manual-invoice-card__header">
              <h2>Fatura Kalemleri</h2>
              <button type="button" className="add-line-button" onClick={addLine}>
                <FaPlus /> Kalem Ekle
              </button>
            </div>

            <div className="manual-invoice-lines">
              {lines.map((line, index) => {
                const lineSubTotal = Number(line.quantity || 0) * Number(line.unitPrice || 0);
                const lineVat = lineSubTotal * (Number(line.vatRate || 0) / 100);
                const lineTotal = lineSubTotal + lineVat;

                return (
                  <div key={line.id} className="manual-invoice-line">
                    <div className="manual-invoice-line__row">
                      <label className="wide">
                        <span>Kalem {index + 1}</span>
                        <input
                          type="text"
                          value={line.description}
                          onChange={(event) => updateLine(line.id, 'description', event.target.value)}
                          placeholder="Açıklama gir"
                        />
                      </label>

                      <label>
                        <span>Miktar</span>
                        <input
                          type="number"
                          min="1"
                          value={line.quantity}
                          onChange={(event) => updateLine(line.id, 'quantity', event.target.value)}
                        />
                      </label>

                      <label>
                        <span>Birim Fiyat</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.unitPrice}
                          onChange={(event) => updateLine(line.id, 'unitPrice', event.target.value)}
                        />
                      </label>

                      <label>
                        <span>KDV Oranı</span>
                        <select
                          value={line.vatRate}
                          onChange={(event) => updateLine(line.id, 'vatRate', event.target.value)}
                        >
                          {VAT_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              %{option}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="manual-invoice-line__total-field">
                        <span>Kalem Toplamı</span>
                        <div className="manual-invoice-line__total">
                          <strong>{formatCurrency(lineTotal)}</strong>
                        </div>
                      </label>

                      <button
                        type="button"
                        className="remove-line-button"
                        onClick={() => removeLine(line.id)}
                        disabled={lines.length === 1}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="manual-invoice-summary">
              <div>
                <span>Ara Toplam</span>
                <strong>{formatCurrency(totals.subTotal)}</strong>
              </div>
              <div>
                <span>Toplam KDV</span>
                <strong>{formatCurrency(totals.totalVat)}</strong>
              </div>
              <div>
                <span>Genel Toplam</span>
                <strong>{formatCurrency(totals.total)}</strong>
              </div>
            </div>

            <div className="manual-invoice-actions">
              <button type="button" className="secondary-button" onClick={handlePreview}>
                Önizlemeyi Gör
              </button>
              <button type="button" className="primary-button" onClick={handleSendInvoice} disabled={INVOICE_SENDING_DISABLED || isSending}>
                {INVOICE_SENDING_DISABLED ? 'Fatura Kes Kapalı' : isSending ? 'Gonderiliyor...' : 'Fatura Kes'}
              </button>
            </div>
          </article>
        </section>
      </div>

      {previewOpen && (
        <div className="manual-invoice-preview-modal" role="presentation" onClick={() => setPreviewOpen(false)}>
          <div
            className="manual-invoice-preview-modal__content"
            role="dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="manual-invoice-preview-modal__header">
              <div>
                <h2>Manuel Fatura Önizleme</h2>
                <p>Fatura kesmeden önce müşteri bilgilerini ve kalemleri son kez kontrol et.</p>
              </div>
              <button type="button" className="manual-invoice-preview-close" onClick={() => setPreviewOpen(false)}>
                ×
              </button>
            </div>

            <div className="manual-invoice-preview-meta">
              <div>
                <span>Firma Ünvanı</span>
                <strong>{customerForm.companyName || '-'}</strong>
              </div>
              <div>
                <span>Vergi Numarası</span>
                <strong>{taxNo || '-'}</strong>
              </div>
              <div>
                <span>Fatura Tipi</span>
                <strong>{invoiceProfile || 'Henüz sorgulanmadı'}</strong>
              </div>
              <div>
                <span>Telefon</span>
                <strong>{customerForm.phone || '-'}</strong>
              </div>
            </div>

            <div className="manual-invoice-preview-address">
              <span>Adres</span>
              <strong>{customerForm.address || '-'}</strong>
            </div>

            <div className="manual-invoice-preview-lines">
              {lines.map((line) => {
                const quantity = Number(line.quantity || 0);
                const unitPrice = Number(line.unitPrice || 0);
                const vatRate = Number(line.vatRate || 0);
                const lineSubTotal = quantity * unitPrice;
                const lineVat = lineSubTotal * (vatRate / 100);
                const lineTotal = lineSubTotal + lineVat;

                return (
                  <div key={line.id} className="manual-invoice-preview-line">
                    <div>
                      <strong>{line.description || '-'}</strong>
                      <span>
                        Miktar: {quantity} | Birim: {formatCurrency(unitPrice)} | KDV: %{vatRate}
                      </span>
                    </div>
                    <strong>{formatCurrency(lineTotal)}</strong>
                  </div>
                );
              })}
            </div>

            <div className="manual-invoice-preview-total">
              <span>
                Ara Toplam: {formatCurrency(totals.subTotal)} | Toplam KDV: {formatCurrency(totals.totalVat)}
              </span>
              <strong>{formatCurrency(totals.total)}</strong>
            </div>

            {customerForm.note && (
              <div className="manual-invoice-preview-note">
                <span>Not</span>
                <p>{customerForm.note}</p>
              </div>
            )}

            <div className="manual-invoice-actions manual-invoice-actions--modal">
              <button type="button" className="secondary-button" onClick={() => setPreviewOpen(false)}>
                Kapat
              </button>
              <button type="button" className="primary-button" onClick={handleSendInvoice} disabled={INVOICE_SENDING_DISABLED || isSending}>
                {INVOICE_SENDING_DISABLED ? 'Fatura Kes Kapalı' : isSending ? 'Gonderiliyor...' : 'Fatura Kes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualInvoiceCreatePage;
